import React, { useState, useCallback, useMemo } from "react";
import { useRecoilValue } from 'recoil';
import { sessionTimingAtom } from '../../../state/assessment';
import Header from "../header";
import Footer from "../footer";
import { getCognitiveTracker } from '../../../services/cognitiveSkills';
import { MODULE_MAPPING, getCognitiveModuleFromCounter, USE_COGNITIVE_SCORING } from './moduleMapping';
import Man from "../../../resources/imgs/assessment/man.svg";
import Manuel from "../../../resources/imgs/assessment/characters/Manuel.png";
import Karl from "../../../resources/imgs/assessment/characters/Karl.png";
import Pamela from "../../../resources/imgs/assessment/characters/Pamela.png";
import Trish from "../../../resources/imgs/assessment/characters/Trish.png";
import Joey from "../../../resources/imgs/assessment/characters/Joey.svg";
import Reina from "../../../resources/imgs/assessment/characters/Reina.png";

import Joey1 from "../../../resources/imgs/assessment/characters/joey1.png"
import Karl1 from "../../../resources/imgs/assessment/characters/karl1.png"
import Manuel1 from "../../../resources/imgs/assessment/characters/manuel1.png"
import Pamela1 from "../../../resources/imgs/assessment/characters/pamela1.png"
import Reina1 from "../../../resources/imgs/assessment/characters/reina1.png"
import Trish1 from "../../../resources/imgs/assessment/characters/trish1.png"

// Import all modules - both original and scored versions
import Module1_0 from "./module1-0"
import { ScoredModule1_1 } from "./module1-1"  // Use scored version
// import Module1_2 from "./module1-2" // Removed - duplicate of Module1_1
import Module1_3 from "./module1-3"
import { ScoredModule1_4 } from "./module1-4"  // Use scored version
import Module1_5 from "./module1-5"
import Module2_1 from "./module2-1"
// import Module2_2 from "./module2-2" // REMOVED - unused, old difficulty-specific version
import Module2_3 from "./module2-3" // Final Orders page - shows after seating arrangement
import Module2_4 from "./module2-4"
import AdaptiveModule2 from "./module2-adaptive"  // Import adaptive module
import Module3_1 from "./module3-1"
// import Module3_2 from "./module3-2" // REMOVED - moved to module2-3 (Final Orders page)
import Module3_3 from "./module3-3"
import Module3_4 from "./module3-4"
import AdaptiveModule3Original from "./module3-3/adaptive-wrapper"  // Use the original wrapper that works with Module3_3
import Module4_1 from "./module4-1"
import { ScoredModule4_2 } from "./module4-2"  // Use scored version
import Module4_3 from "./module4-3"
import { ScoredModule4_4 } from "./module4-4"  // Use scored version
import Module4_5 from "./module4-5"
import { ScoredModule4_6 } from "./module4-6"  // Use scored version
import Module4_7 from "./module4-7"
import Module5_1 from "./module5-1"
import { ScoredModule5_2 } from "./module5-2"  // Use scored version
import Module5_3 from "./module5-3"
import Module6_1 from "./module6-1"
import Module6_2 from "./module6-2"
import Module6_3 from "./module6-3"
import AdaptiveModule6 from "./module6-adaptive"  // Import adaptive module
import Module7_1 from "./module7-1"
import Module7_2 from "./module7-2"
import Module7_3 from "./module7-3"
import AdaptiveModule7 from "./module7-adaptive"  // Import adaptive module
import Module8_1 from "./module8-1"
import Module8_2 from "./module8-2"
import Module8_3 from "./module8-3"
import Module8_4 from "./module8-4"
import Module8_5 from "./module8-5"
import Module8_6 from "./module8-6"
import Module8_7 from "./module8-7"
import Module8_8 from "./module8-8"

import "@egjs/react-flicking/dist/flicking.css";
import "@egjs/react-flicking/dist/flicking-inline.css";
import "./style.css";
import { generateRandomPhoneNumber, reversePhoneNumber } from './utils/assessmentUtils';

// Alias for backward compatibility - use original wrapper
const AdaptiveModule3 = AdaptiveModule3Original;

// Use dynamic imports for dishes to avoid compilation issues with large SVGs
const getDishImage = (dishNumber) => `${process.env.PUBLIC_URL}/images/dishes/dish${dishNumber}.svg`;

// Constants
const INITIAL_CARD_DATA = [
  { img: Manuel, otherImg: Manuel1, name: "Manuel", dish: [0, 1, 2], visible: true },
  { img: Pamela, otherImg: Pamela1, name: "Pamela", dish: [3, 0, 4], visible: true },
  { img: Trish, otherImg: Trish1, name: "Trish", dish: [1, 3, 5], visible: true },
  { img: Karl, otherImg: Karl1, name: "Karl", dish: [4, 5, 0], visible: true },
  { img: Joey, otherImg: Joey1, name: "Joey", dish: [5, 3, 4], visible: true },
  { img: Reina, otherImg: Reina1, name: "Reina", dish: [2, 5, 0], visible: true },
];

const INITIAL_EMOTIONAL_DATA = [
  { dish: getDishImage(1), emotional: Man, type: 0 },
  { dish: getDishImage(2), emotional: Manuel, type: 0 },
  { dish: getDishImage(3), emotional: Karl, type: 0 },
  { dish: getDishImage(4), emotional: Manuel, type: 0 },
  { dish: getDishImage(5), emotional: Karl, type: 0 },
  { dish: getDishImage(6), emotional: Manuel, type: 0 },
];

const INITIAL_WORDS = [
  "Book", "Cousins", "Parents", "Chair", "Laptop", "Elephant", "Mountain",
  "Computer", "Cat", "Grandparents", "Siblings", "Ocean", "Guita", "Moon",
  "River", "Train", "Children", "Rencil", "Pizza", "Glasses",
];

export default function Module({ onComplete }) {
  // State management - removed global variables
  const [cnt, setCnt] = useState(-1);
  const [cardData, setCardData] = useState(INITIAL_CARD_DATA);
  const [emotionalData, setEmotionalData] = useState(INITIAL_EMOTIONAL_DATA);
  const [emotionalIdx, setEmotionalIdx] = useState(2);
  const [imgIndex, setImageIndex] = useState([]);
  const [words, setWords] = useState(INITIAL_WORDS);
  const [selectedWords, setSelectedWords] = useState([]);
  const [res, setRes] = useState([]);
  const [flag, setFlag] = useState(0);

  // Access session timing data for PSSI/ASI calculations
  const sessionTiming = useRecoilValue(sessionTimingAtom);
  const [activeNum, setActiveNum] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [groupNumber, setGroupNumber] = useState([]);
  
  // Generate random phone number for Module 8 on mount
  const [targetPhoneNumber] = useState(() => generateRandomPhoneNumber());
  const [targetPhoneNumberBackwards] = useState(() => reversePhoneNumber(targetPhoneNumber));

  // Cognitive assessment state
  const [cognitiveTracker] = useState(() => getCognitiveTracker());
  const [assessmentResults, setAssessmentResults] = useState({});
  const [assessmentResultId, setAssessmentResultId] = useState(null);
  const [assessmentStartTime] = useState(Date.now());
  const isInCognitiveMode = USE_COGNITIVE_SCORING; // Always use cognitive mode
  // Cognitive module tracking handled by moduleMapping

  // Start assessment on mount
  React.useEffect(() => {
    const startAssessment = async () => {
      const userId = localStorage.getItem('userId');
      if (userId && !assessmentResultId) {
        try {
          const apiBase = 'https://nbassessment-l8rxu.ondigitalocean.app/api'; // Temporarily hardcoded
          // const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
          const apiUrl = `${apiBase}/assessments/start`;
          console.log('🚀 Starting assessment - API URL:', apiUrl);
          console.log('🔍 API Base:', apiBase);
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
          });
          
          if (response.ok) {
            const data = await response.json();
            setAssessmentResultId(data.assessment.result_id);
            console.log('🎯 Assessment started with ID:', data.assessment.result_id);
          }
        } catch (error) {
          console.error('Failed to start assessment:', error);
        }
      }
    };
    
    startAssessment();
  }, [assessmentResultId]);

  // Scroll to top when transitioning between modules
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [cnt]);

  // Optimized handlers with useCallback
  const Choose = useCallback((cnt) => {
    let arr = new Array(40).fill(0);
    arr[cnt] = 1;
    setRes(arr);
  }, []);

  const SelectDish = useCallback((idx) => {
    setEmotionalData(prev => prev.map((item, index) =>
      index === emotionalIdx ? { ...item, type: idx } : item
    ));
  }, [emotionalIdx]);

  const handleDrop = useCallback((val, idx) => {
    let data = [...cardData];
    const removeIdx = imgIndex.indexOf(val);
    const newImgIndex = [...imgIndex];
    
    // If the item was previously placed somewhere else, clear that position
    if (removeIdx !== -1) {
      newImgIndex[removeIdx] = undefined;
    }
    
    // Check if the target slot is already occupied
    const previousItemAtSlot = newImgIndex[idx];
    
    // If there was a previous item in this slot, make it visible again
    if (previousItemAtSlot !== undefined) {
      data[previousItemAtSlot].visible = true;
    }
    
    // Hide the item being placed and place it in the target slot
    data[val].visible = false;
    newImgIndex[idx] = val;
    setImageIndex(newImgIndex);

    // Check if all positions are filled
    let flag = 1;
    for (let i = 0; i < 6; i++) {
      if (newImgIndex[i] === undefined) {
        flag = 0;
        break;
      }
    }

    if (flag) {
      setFlag(1);
    } else {
      setFlag(0);
    }

    setCardData(data);
  }, [cardData, imgIndex]);

  const resetSeat = useCallback(() => {
    setCardData(prev => prev.map(item => ({ ...item, visible: true })));
    setImageIndex([]);
    setFlag(0);
  }, []);

  const resetCard = useCallback(() => {
    setGroupNumber([]);
  }, []);

  // Word manipulation callbacks for modules that need them
  const wordDrop = useCallback((idx) => {
    const wordToMove = words[idx];
    setWords(prev => prev.filter((_, index) => index !== idx));
    setSelectedWords(prev => [...prev, wordToMove]);
  }, [words]);

  const removeWords = useCallback((idx) => {
    const wordToRemove = selectedWords[idx];
    setSelectedWords(prev => prev.filter((_, index) => index !== idx));
    setWords(prev => [...prev, wordToRemove]);
  }, [selectedWords]);

  const selectGroupNumber = useCallback((idx) => {
    if (groupNumber.length >= 2) return;
    setGroupNumber(prev => [...prev, idx]);
  }, [groupNumber.length]);

  // Cognitive assessment handlers
  const handleModuleComplete = useCallback(async (completionData) => {
    console.log(`🎯🎯🎯 handleModuleComplete called with:`, completionData);
    console.log(`🎯 Current counter before navigation: ${cnt}`);
    
    const finalState = cognitiveTracker.getState();
    const percentiles = cognitiveTracker.calculatePercentiles();
    
    console.log(`🎯 Cognitive tracker state:`, finalState);
    console.log(`🎯 Calculated percentiles:`, percentiles);
    
    setAssessmentResults(prev => ({
      ...prev,
      [completionData.moduleType]: {
        percentiles,
        finalState,
        ...completionData
      }
    }));
    
    // Save module attempt to database if we have an assessment ID
    if (assessmentResultId && completionData.moduleType) {
      const userId = localStorage.getItem('userId');
      const moduleHistory = finalState.moduleHistory[completionData.moduleType];
      
      if (moduleHistory && userId) {
        try {
          // Filter trial data for this specific module
          const moduleTrialData = sessionTiming.trialData?.filter(
            trial => trial.module_id === completionData.moduleType
          ) || [];

          console.log(`📊 Submitting module ${completionData.moduleType} with ${moduleTrialData.length} trial records`);

          const response = await fetch(`${'https://nbassessment-l8rxu.ondigitalocean.app/api'}/assessments/module-attempt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resultId: assessmentResultId,
              userId,
              moduleId: completionData.moduleType,
              questionsAnswered: moduleHistory.questionsAnswered || 0,
              correctAnswers: moduleHistory.correctAnswers || 0,
              pointsEarned: moduleHistory.attempts?.reduce((sum, a) => sum + (a.points || 0), 0) || 0,
              maxPointsPossible: completionData.maxPossiblePoints || 0,
              difficultySequence: moduleHistory.attempts?.map(a => a.difficulty) || [],
              instanceIds: moduleHistory.attempts?.map(a => a.instanceId).filter(Boolean) || [],
              responses: moduleHistory.attempts || [],
              duration: Math.floor((Date.now() - assessmentStartTime) / 1000),
              trialData: moduleTrialData  // Include trial-level timing data
            })
          });

          if (response.ok) {
            const result = await response.json();
            console.log(`✅ Module ${completionData.moduleType} results saved (${result.trialDataSaved} trial records)`);
          }
        } catch (error) {
          console.error(`Failed to save module ${completionData.moduleType} results:`, error);
        }
      }
    }
    
    console.log(`🎯 Module ${completionData.moduleType} completed, looking for next module...`);
    
    // Navigation after module completion
    // Each MODULE (1-8) should show its progress page before the next module's instructions
    const moduleCompletionNavigation = {
      '1A': 2,   // Module 1A → Module 1B directions (still within Module 1)
      '1B': 4,   // Module 1B → Module 1 progress page (Module1_5)
      '2': 7,    // Module 2 → Module 2-3 (Final Orders)
      '3': 13,   // Module 3 → Module 3 progress page (Module3_4)
      '4A': 16,  // Module 4A → Module 4B directions (still within Module 4)
      '4B': 18,  // Module 4B → Module 4C directions (still within Module 4)  
      '4C': 20,  // Module 4C → Module 4 progress page (Module4_7)
      '5': 23,   // Module 5 → Module 5 progress page (Module5_3)
      '6': 26,   // Module 6 → Module 6 progress page (Module6_3)
      '7': 29,   // Module 7 → Module 7 progress page (Module7_3)
      '8': 37,   // Module 8 → Module 8 final progress page (Module8_8)
    };
    
    const nextCounter = moduleCompletionNavigation[completionData.moduleType];
    
    if (nextCounter !== undefined) {
      console.log(`🎯 Module ${completionData.moduleType} completed, navigating to counter ${nextCounter}`);
      setCnt(nextCounter);
      return;
    }
    
    // For other adaptive modules, find next cognitive module
    // Define the correct order of modules
    const moduleOrder = ['1A', '1B', '2', '3', '4A', '4B', '4C', '5', '6', '7', '8'];
    const cognitiveModules = moduleOrder.map(key => MODULE_MAPPING[key]).filter(Boolean);
    console.log(`🎯 All cognitive modules:`, cognitiveModules.map(m => m.cognitiveType));
    
    const currentIndex = cognitiveModules.findIndex(m => m.cognitiveType === completionData.moduleType);
    console.log(`🎯 Current module "${completionData.moduleType}" found at index: ${currentIndex}`);
    
    const nextModule = cognitiveModules[currentIndex + 1];
    console.log(`🎯 Next module:`, nextModule);
    
    if (nextModule) {
      const nextCounter = nextModule.originalModules[0];
      console.log(`🎯 Next cognitive module: ${nextModule.cognitiveType}, jumping to counter ${nextCounter}`);
      console.log(`🎯 Setting counter from ${cnt} to ${nextCounter}`);
      setCnt(nextCounter);
    } else {
      console.log(`🎯 Assessment complete! No more cognitive modules.`);
      setCnt(1000); // Set to a high number to trigger completion
    }
  }, [cognitiveTracker, cnt, assessmentResultId, assessmentStartTime]);

  // Handle final assessment completion
  const handleAssessmentComplete = useCallback(async () => {
    console.log('🎯 Assessment complete! Saving results...');
    
    // Calculate total assessment time in seconds
    const totalTimeSeconds = Math.floor((Date.now() - assessmentStartTime) / 1000);
    
    // Get user data
    const userId = localStorage.getItem('userId');
    
    if (userId && assessmentResultId) {
      try {
        // Complete the assessment in database
        const response = await fetch(`${'https://nbassessment-l8rxu.ondigitalocean.app/api'}/assessments/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resultId: assessmentResultId,
            totalTimeSeconds
          })
        });
        
        if (!response.ok) {
          console.error('Failed to save assessment results to database');
        } else {
          const data = await response.json();
          console.log('✅ Assessment results saved successfully:', data);
        }
      } catch (error) {
        console.error('Error saving assessment results:', error);
      }
    }
    
    // Call the parent onComplete handler
    if (onComplete) {
      onComplete();
    }
  }, [assessmentResultId, assessmentStartTime, onComplete]);

  // Check if current counter maps to a cognitive module
  const cognitiveModuleInfo = useMemo(() => {
    if (!isInCognitiveMode) return null;
    const info = getCognitiveModuleFromCounter(cnt);
    console.log(`🔍 Counter ${cnt} cognitive mapping:`, info);
    return info;
  }, [cnt, isInCognitiveMode]);

  // Module configuration to replace massive if-else chain
  const moduleConfig = useMemo(() => ({
    [-1]: { component: Module1_0, props: { setData: setCardData, nextPage: () => setCnt(cnt + 1) } },
    [0]: { component: ScoredModule1_1, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '1A',
      cardData 
    } },
    // [1]: Module1_2 - REMOVED (duplicate of Module1_1)
    [2]: { component: Module1_3, props: { nextPage: () => { setRes([]); setCnt(cnt + 1); }, res, Choose, emotionalIdx, setEmotionalIdx, emotionalData, SelectDish, cardData } },
    [3]: { component: ScoredModule1_4, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '1B',
      cardData, 
      handleDrop, 
      imgIndex, 
      flag 
    } },
    [4]: { component: Module1_5, props: { nextPage: () => setCnt(5), progress: 1 } }, // Jump to Module 2 adaptive
    [5]: { component: Module2_1, props: { nextPage: () => setCnt(6) } }, // Jump to Module 2 adaptive
    [6]: { component: AdaptiveModule2, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '2',
      cardData
    } },
    [7]: { component: Module2_3, props: { nextPage: () => setCnt(8), cardData } }, // Module 2 Final Orders
    [8]: { component: Module2_4, props: { nextPage: () => { console.log('🔄 Module2_4 nextPage clicked, going from 8 to 9'); setCnt(9); }, progress: 2 } },
    [9]: { component: Module3_1, props: { nextPage: () => setCnt(12) } }, // Module 3 directions - jump to adaptive
    [10]: { component: null, props: {} }, // Placeholder - Module3_2 removed (was Final Orders, now in Module 2)
    [11]: { component: null, props: {} }, // Placeholder - no longer used
    [12]: { component: AdaptiveModule3, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '3'
    } },
    [13]: { component: Module3_4, props: { nextPage: () => setCnt(14), progress: 3 } }, // Module 3 progress page
    [14]: { component: Module4_1, props: { nextPage: () => setCnt(cnt + 1) } },
    [15]: { component: ScoredModule4_2, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '4A',
      cardData, 
      flag, 
      res, 
      choose: Choose 
    } },
    [16]: { component: Module4_3, props: { nextPage: () => setCnt(cnt + 1) } },
    [17]: { component: ScoredModule4_4, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '4B',
      flag, 
      res, 
      cardData: cardData.map(card => card.name), 
      choose: Choose 
    } },
    [18]: { component: Module4_5, props: { nextPage: () => setCnt(cnt + 1) } },
    [19]: { component: ScoredModule4_6, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '4C',
      flag, 
      res, 
      cardData, // Pass full card objects for Module 4C
      choose: Choose 
    } },
    [20]: { component: Module4_7, props: { nextPage: () => setCnt(cnt + 1), progress: 4 } },
    [21]: { component: Module5_1, props: { nextPage: () => setCnt(cnt + 1) } },
    [22]: { component: ScoredModule5_2, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '5',
      flag, 
      res, 
      choose: Choose 
    } },
    [23]: { component: Module5_3, props: { nextPage: () => setCnt(24), progress: 5 } },
    [24]: { component: Module6_1, props: { nextPage: () => setCnt(25) } },
    [25]: { component: AdaptiveModule6, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '6',
      cardData
    } },
    [26]: { component: Module6_3, props: { nextPage: () => setCnt(cnt + 1), progress: 6 } },
    [27]: { component: Module7_1, props: { nextPage: () => setCnt(cnt + 1) } },
    [28]: { component: AdaptiveModule7, props: { 
      onModuleComplete: handleModuleComplete,
      moduleType: '7'
    } },
    [29]: { component: Module7_3, props: { nextPage: () => setCnt(cnt + 1), progress: 7 } },
    [30]: { component: Module8_1, props: { nextPage: () => setCnt(cnt + 1) } },
    [31]: { component: Module8_2, props: { 
      nextPage: () => setCnt(cnt + 1), 
      setNumber: setPhoneNumber, 
      phoneNumber, 
      targetPhoneNumber
      // No scoring - this is just the phone number display
    } },
    [32]: { component: Module8_3, props: { 
      nextPage: () => { setPhoneNumber(""); setCnt(cnt + 1); }, 
      setNumber: setPhoneNumber, 
      phoneNumber, 
      targetPhoneNumber,
      recordScore: (isCorrect, details) => {
        const points = details?.customPoints || 0;
        console.log(`📊 Module 8-3 Score: ${points} points`);
        cognitiveTracker.recordAnswer('8', isCorrect, 2, null, points);
      }
    } },
    [33]: { component: Module8_4, props: { 
      nextPage: () => setCnt(cnt + 1), 
      res, 
      Choose, 
      cardData
      // No scoring - this is the guest interruption visual test
    } },
    [34]: { component: Module8_5, props: { 
      nextPage: () => { setPhoneNumber(""); setCnt(cnt + 1); }, 
      setNumber: setPhoneNumber, 
      phoneNumber, 
      targetPhoneNumber,
      recordScore: (isCorrect, details) => {
        const points = details?.customPoints || 0;
        console.log(`📊 Module 8-5 Score: ${points} points`);
        cognitiveTracker.recordAnswer('8', isCorrect, 2, null, points);
      }
    } },
    [35]: { component: Module8_6, props: { 
      nextPage: () => { setGroupNumber([]); setCnt(cnt + 1); }, 
      cardData, 
      resetCard, 
      selectGroupNumber, 
      groupNumber
      // No scoring - this is the group seating arrangement
    } },
    [36]: { component: Module8_7, props: { 
      nextPage: () => setCnt(cnt + 1), 
      setNumber: setPhoneNumber, 
      phoneNumber, 
      targetPhoneNumber, 
      targetPhoneNumberBackwards,
      recordScore: (isCorrect, details) => {
        const points = details?.customPoints || 0;
        console.log(`📊 Module 8-7 Score: ${points} points`);
        cognitiveTracker.recordAnswer('8', isCorrect, 3, null, points); // Higher difficulty for backwards
      }
    } },
    [37]: { component: Module8_8, props: { 
      nextPage: () => {
        // Save Module 8 results before completing assessment
        handleModuleComplete({ moduleType: '8' });
        setCnt(cnt + 1);
      }, 
      progress: 8,
      isLastModule: true,
      onComplete: () => handleAssessmentComplete()
    } },
  }), [cnt, res, cardData, Choose, emotionalIdx, emotionalData, SelectDish, resetSeat, handleDrop, imgIndex, flag, phoneNumber, groupNumber, selectGroupNumber, resetCard, targetPhoneNumber, targetPhoneNumberBackwards]);

  // Render current module
  const renderCurrentModule = useCallback(() => {
    console.log(`🔍 renderCurrentModule: counter=${cnt}, cognitiveModuleInfo=`, cognitiveModuleInfo);
    
    // Debug cardData when Module 2 is about to render
    if (cnt === 6) {
      console.log('🎭 About to render Module 2 with cardData:', cardData?.map(c => c.name));
    }
    
    // Debug Module 2 progress and Module 3 pages
    if (cnt === 8) {
      console.log('🎯 Rendering Module 2 progress page (counter 8)');
    }
    if (cnt === 9) {
      console.log('🎯 Rendering Module 3 directions (counter 9)');
    }
    if (cnt === 10) {
      console.log('🎯 Rendering Module 3 intro (counter 10)');
    }
    if (cnt === 11) {
      console.log('🎯 Rendering Module 3 pre-adaptive (counter 11)');
    }
    if (cnt === 12) {
      console.log('🎯 Rendering Module 3 adaptive (counter 12)');
    }
    
    // Debug Module 4 pages
    if (cnt === 17) {
      console.log('🎯 Rendering Module 4B (name recognition) at counter 17');
    }
    if (cnt === 18) {
      console.log('🎯 Rendering Module 4C directions (Module4_5) at counter 18');
    }
    if (cnt === 19) {
      console.log('🎯 Rendering Module 4C (face-name matching) at counter 19');
    }
    
    // Check if we're in cognitive mode and this counter maps to a cognitive module
    if (isInCognitiveMode && cognitiveModuleInfo) {
      const { cognitiveType, config, isFirstInModule } = cognitiveModuleInfo;
      console.log(`🎯 Cognitive module detected: ${cognitiveType}, isFirstInModule=${isFirstInModule}, hasComponent=${!!config.component}, isAdaptive=${config.isAdaptive}, counter=${cnt}`);
      
      // If this cognitive module has no component, fall back to regular modules
      if (!config.component) {
        console.log(`🎯 Cognitive module ${cognitiveType} has no component, falling back to regular modules`);
        // Don't return here, let it fall through to regular module handling
      } else if (config.component && config.isAdaptive) {
        // Handle adaptive modules - run adaptive component ONLY at the specific counter
        // Module 2: runs at counter 6 (adaptive component)
        // Module 3: runs at counter 12 (adaptive component after directions/intro)
        // Module 6: runs at counter 25 (adaptive component after directions)
        // Module 7: runs at counter 28 (adaptive component after directions)
        if ((cognitiveType === '2' && cnt === 6) ||
            (cognitiveType === '3' && cnt === 12) ||
            (cognitiveType === '6' && cnt === 25) ||
            (cognitiveType === '7' && cnt === 28)) {
          const CognitiveComponent = config.component;
          console.log(`🎯 Running adaptive component for ${cognitiveType} at counter ${cnt}`);
          console.log(`🎯 Using component:`, CognitiveComponent.name);
          // Return the adaptive component and EXIT early to prevent double rendering
          return (
            <CognitiveComponent 
              onModuleComplete={handleModuleComplete}
              moduleType={cognitiveType}
              cardData={cardData}  // Pass cardData for Module 2 to use dynamic characters
            />
          );
        }
        // For adaptive modules at non-adaptive counters (like Module 3 at counters 9, 10, 11),
        // fall through to regular module handling
        console.log(`🎯 Adaptive module ${cognitiveType} at counter ${cnt} - using regular module`);
        // Don't return here, let it fall through
      } else if (config.component && !config.isAdaptive) {
        // Handle non-adaptive cognitive modules
        // Special case for Module 1A: run Module1_1 at counter 0 (not the first counter -1)
        if (cognitiveType === '1A' && cnt === 0) {
          const CognitiveComponent = config.component;
          console.log(`🎯 Running Module 1A component at counter ${cnt}`);
          return (
            <CognitiveComponent 
              onModuleComplete={handleModuleComplete}
              moduleType={cognitiveType}
            />
          );
        }
        // For modules with directions pages, run cognitive component at correct counter
        else if ((cognitiveType === '1B' && cnt === 3) || 
                 (cognitiveType === '4A' && cnt === 15) || 
                 (cognitiveType === '4B' && cnt === 17) || 
                 (cognitiveType === '4C' && cnt === 19) ||
                 (cognitiveType === '5' && cnt === 22)) {
          const CognitiveComponent = config.component;
          console.log(`🎯 Running non-adaptive component for ${cognitiveType} at counter ${cnt} (after directions)`);
          
          // Pass necessary props based on module type
          let moduleProps = {
            onModuleComplete: handleModuleComplete,
            moduleType: cognitiveType
          };
          
          if (cognitiveType === '1B') {
            // Module 1B (Module1_4) needs cardData, handleDrop, imgIndex, flag
            moduleProps = {
              ...moduleProps,
              cardData,
              handleDrop,
              imgIndex,
              flag
            };
          } else if (cognitiveType.startsWith('4')) {
            // Module 4A, 4B, 4C need cardData for face/name recognition
            moduleProps = {
              ...moduleProps,
              cardData
            };
          }
          
          return (
            <CognitiveComponent {...moduleProps} />
          );
        }
      }
    }

    // Fallback to original module configuration
    const moduleConf = moduleConfig[cnt];
    if (!moduleConf || !moduleConf.component) {
      // Handle null component (e.g., placeholder at counter 11)
      return null;
    }

    const ModuleComponent = moduleConf.component;
    return <ModuleComponent {...moduleConf.props} />;
  }, [cnt, moduleConfig, isInCognitiveMode, cognitiveModuleInfo, handleModuleComplete, assessmentResults]);

  return (
    <div className="module1-container">
      <Header />
      
      {/* Cognitive Assessment Info */}
      {cognitiveModuleInfo && (
        <div className="assessment-info-bar" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          background: 'linear-gradient(135deg, #007bff, #0056b3)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '25px',
          fontSize: '14px',
          fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#28a745' }}></div>
            Module {cognitiveModuleInfo.cognitiveType}: {MODULE_MAPPING[cognitiveModuleInfo.cognitiveType]?.description}
          </div>
        </div>
      )}
      
      {renderCurrentModule()}
      <Footer />
    </div>
  );
}
